import { ref, computed, type Ref } from 'vue'

const currentPage: Ref<number> = ref(1)
const totalPages: Ref<number> = ref(1)
const totalResults: Ref<number> = ref(0)
const itemsPerPage: Ref<number> = ref(10)
const disabled: Ref<boolean> = ref(false)
const maxVisibleButtons: Ref<number> = ref(5)

/** Results-only mode (e.g. infinite scroll): show count, no page controls. */
const resultsShown: Ref<number> = ref(0)
const resultsTotal: Ref<number | null> = ref(null)
const resultsLabel: Ref<string> = ref('items')

let onPageChangeCallback: ((page: number) => void) | null = null
const hasRegistration: Ref<boolean> = ref(false)
const isResultsOnlyMode: Ref<boolean> = ref(false)
/** When true (set via setPagination), show the bar even if totalPages is 1 (e.g. OTS Details). */
const paginationAlwaysVisible: Ref<boolean> = ref(false)

const visible = computed(
  () =>
    hasRegistration.value &&
    (isResultsOnlyMode.value || totalPages.value > 1 || paginationAlwaysVisible.value)
)

/**
 * Composable for fixed bottom pagination bar: table views register pagination state
 * when mounted and clear on unmount. BottomPaginationBar reads this state.
 * Call clear() on unmount when registering.
 */
export function useBottomPagination() {
  function setPagination(params: {
    currentPage: number
    totalPages: number
    totalResults: number
    itemsPerPage: number
    onPageChange: (page: number) => void
    disabled?: boolean
    maxVisibleButtons?: number
    /** Show fixed bar when there is only one page (default false). */
    alwaysVisible?: boolean
  }) {
    isResultsOnlyMode.value = false
    paginationAlwaysVisible.value = params.alwaysVisible ?? false
    currentPage.value = params.currentPage
    totalPages.value = params.totalPages
    totalResults.value = params.totalResults
    itemsPerPage.value = params.itemsPerPage
    disabled.value = params.disabled ?? false
    maxVisibleButtons.value = params.maxVisibleButtons ?? 5
    onPageChangeCallback = params.onPageChange
    hasRegistration.value = true
  }

  function setResultsInfo(params: { shown: number; total?: number | null; label?: string }) {
    isResultsOnlyMode.value = true
    paginationAlwaysVisible.value = false
    resultsShown.value = params.shown
    resultsTotal.value = params.total ?? null
    resultsLabel.value = params.label ?? 'items'
    hasRegistration.value = true
  }

  function clear() {
    hasRegistration.value = false
    isResultsOnlyMode.value = false
    paginationAlwaysVisible.value = false
    onPageChangeCallback = null
    currentPage.value = 1
    totalPages.value = 1
    totalResults.value = 0
    itemsPerPage.value = 10
    disabled.value = false
    maxVisibleButtons.value = 5
    resultsShown.value = 0
    resultsTotal.value = null
    resultsLabel.value = 'items'
  }

  function handlePageChange(page: number) {
    if (onPageChangeCallback) {
      currentPage.value = page
      onPageChangeCallback(page)
    }
  }

  return {
    currentPage,
    totalPages,
    totalResults,
    itemsPerPage,
    disabled,
    maxVisibleButtons,
    visible,
    setPagination,
    setResultsInfo,
    clear,
    handlePageChange,
    isResultsOnlyMode,
    resultsShown,
    resultsTotal,
    resultsLabel
  }
}
